import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './GenderGlyph.module.css';

/**
 * AB#1840 (PBI-B) / AB#1838 — Customer-side gender matrix.
 *
 * Shared chip used wherever Devanza renders the gender axis of a service
 * or salon. Bilingual via `t()`. RTL handled via logical properties in
 * the CSS (no physical margins, no class-name flipping).
 *
 * The BE serializes `ServiceType` as a STRING per Faris F-Q2=A (decision
 * pass 3, 2026-05-31): `"MenOnly" | "WomenOnly" | "Everyone"`. We accept
 * both the string form (preferred) and the legacy numeric form
 * (1=MenOnly, 2=WomenOnly, 3=Everyone) so older callers keep working
 * during the BE rollout window.
 *
 * Per Q6 = A (founder Alaa, 2026-05-31): the Mixed (Everyone) chip
 * renders explicitly — Devanza positions as a gender-neutral marketplace
 * so we do NOT hide it for visual quiet.
 *
 * i18n keys (added to LanguageContext inline translations):
 *   - services.gender.menOnly   → "Men only" / "رجالي"
 *   - services.gender.womenOnly → "Women only" / "نسائي"
 *   - services.gender.mixed     → "Mixed" / "للجميع"
 *   - services.gender.aria.menOnly   → "For men only" / "للرجال فقط"
 *   - services.gender.aria.womenOnly → "For women only" / "للسيدات فقط"
 *   - services.gender.aria.mixed     → "For everyone" / "للجميع"
 */

export type ServiceTypeValue = 'MenOnly' | 'WomenOnly' | 'Everyone';

/**
 * Normalises whatever shape the BE returned (string OR numeric — Devanza
 * has both in the wild as of the 2026-05-13 services session) into the
 * canonical string the chip renders against. Returns `null` for missing
 * / unknown values so the caller can decline to render rather than crash.
 */
export function normalizeServiceType(
    raw: ServiceTypeValue | number | string | null | undefined,
): ServiceTypeValue | null {
    if (raw == null) return null;
    if (typeof raw === 'string') {
        if (raw === 'MenOnly' || raw === 'WomenOnly' || raw === 'Everyone') {
            return raw;
        }
        return null;
    }
    if (typeof raw === 'number') {
        switch (raw) {
            case 1: return 'MenOnly';
            case 2: return 'WomenOnly';
            case 3: return 'Everyone';
            default: return null;
        }
    }
    return null;
}

export interface GenderGlyphProps {
    /** Accepts string ("MenOnly"…) OR numeric (1/2/3) per BE drift tolerance. */
    serviceType: ServiceTypeValue | number | string | null | undefined;
    /**
     * When true, hide the textual label and render only the glyph. Used
     * by dense surfaces (e.g. calendar staff column) where the chip's
     * `aria-label` is enough for a11y. Defaults to false — label visible.
     */
    glyphOnly?: boolean;
    /** Optional class override for the wrapping `<span>`. */
    className?: string;
}

const GenderGlyph: React.FC<GenderGlyphProps> = ({
    serviceType,
    glyphOnly = false,
    className,
}) => {
    const { t } = useLanguage();

    const normalised = normalizeServiceType(serviceType);
    if (!normalised) {
        // Defensive — pre-PBI-B legacy data may carry null. Skip render
        // rather than crash; caller's parent already has its own label.
        return null;
    }

    let glyph: string;
    let labelKey: string;
    let ariaKey: string;
    let variantClass: string;

    switch (normalised) {
        case 'MenOnly':
            glyph = '♂'; // ♂ Male sign
            labelKey = 'services.gender.menOnly';
            ariaKey = 'services.gender.aria.menOnly';
            variantClass = styles.chipMenOnly;
            break;
        case 'WomenOnly':
            glyph = '♀'; // ♀ Female sign
            labelKey = 'services.gender.womenOnly';
            ariaKey = 'services.gender.aria.womenOnly';
            variantClass = styles.chipWomenOnly;
            break;
        case 'Everyone':
            glyph = '⚥'; // ⚥ Male and female sign
            labelKey = 'services.gender.mixed';
            ariaKey = 'services.gender.aria.mixed';
            variantClass = styles.chipEveryone;
            break;
    }

    return (
        <span
            className={`${styles.chip} ${variantClass} ${className ?? ''}`.trim()}
            aria-label={t(ariaKey)}
            role="img"
        >
            <span aria-hidden="true" className={styles.glyph}>{glyph}</span>
            {!glyphOnly && <span>{t(labelKey)}</span>}
        </span>
    );
};

export default GenderGlyph;
